import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { toast } from 'sonner';
import { getApiErrorDetail, resolveSiteAddressFields } from '@klareospace/pharos-lib-front-utils';
import { buildNullablePhoneNumberSchema } from '@klareospace/pharos-lib-front-utils/phone-validation';
import type { GeocodingSource, GeoPoint, PostalAddress, Site } from '@klareospace/pharos-lib-types';
import { SITE_TYPE } from '@klareospace/pharos-lib-types';
import { normalizeCountryCode, ratioToPercent } from '@klareospace/pharos-lib-utils';

import type { SiteType } from '@klareospace/pharos-lib-types';

import type { UpdateSiteFormInput } from '../schemas/update-site.schema';
import { buildSiteLocation, hasAddressData, hasPhoneData } from '../utils/site-form.utils';
import { useResetFormOnSiteUpdate } from './use-reset-form-on-site-update';
import { useUpdateSite } from './use-update-site';

// ─── Types ────────────────────────────────────────────────────────────────────

export type { UpdateSiteFormInput };

export interface UseUpdateSiteFormReturn {
  form: UseFormReturn<UpdateSiteFormInput>;
  isDirty: boolean;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (data: UpdateSiteFormInput) => void;
  resetKey: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toAddressDefaults(address: Partial<PostalAddress> | undefined): UpdateSiteFormInput['address'] {
  const defaults: UpdateSiteFormInput['address'] = {
    additionalInformations: '',
    city: '',
    complement: '',
    country: '',
    digicode: '',
    floor: '',
    hasElevator: false,
    houseNumber: '',
    postalCode: '',
    street: '',
  };
  if (!address) return defaults;
  const result = Object.assign(defaults, address) as UpdateSiteFormInput['address'];
  result.country = normalizeCountryCode(result.country);
  return result;
}

/**
 * Narrows a GeocodingSource to only the values accepted by the form schema.
 * 'client' and other provider-specific values are treated as undefined (no pre-selection).
 */
function resolveFormGeocodingSource(source: GeocodingSource | undefined): 'autocomplete' | 'manual' | undefined {
  if (source === 'autocomplete' || source === 'manual') return source;
  return undefined;
}

/**
 * Extracts latitude/longitude from an optional GeoCoordinates object.
 * Separated to keep toDefaultValues below the cyclomatic complexity limit.
 */
function resolveCoordinateValues(coordinates: Partial<GeoPoint> | undefined): Partial<GeoPoint> {
  return {
    latitude: coordinates?.latitude,
    longitude: coordinates?.longitude,
  };
}

function toDefaultValues(site: Site): UpdateSiteFormInput {
  const { address, coordinates } = resolveSiteAddressFields(site);
  const geocodingScore = coordinates !== undefined ? ratioToPercent(coordinates.score, 0) : 0;
  return {
    address: toAddressDefaults(address),
    code: site.code,
    color: site.color,
    coordinates: resolveCoordinateValues(coordinates),
    customerSupportPhone: site.customerSupportPhone ?? null,
    geocodingScore,
    geocodingSource: resolveFormGeocodingSource(coordinates?.source),
    name: site.name,
    phone: site.phone ?? null,
    spaceId: site.spaceId,
    type: site.type,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUpdateSiteForm(site: Site): UseUpdateSiteFormReturn {
  const { t } = useTranslation('settings');
  const updateSite = useUpdateSite(site.id);

  const schema = useMemo(() => {
    const phoneSchema = buildNullablePhoneNumberSchema({ invalid: t('sites.settings.validation.phoneInvalid') });

    return z.object({
      address: z.object({
        additionalInformations: z.string(),
        city: z.string(),
        complement: z.string(),
        country: z.string(),
        digicode: z.string(),
        floor: z.string(),
        hasElevator: z.boolean(),
        houseNumber: z.string(),
        postalCode: z.string(),
        street: z.string(),
      }),
      code: z.string().trim().min(1, t('sites.settings.validation.codeRequired')),
      color: z.string().min(1, t('sites.settings.validation.colorRequired')),
      coordinates: z.object({
        latitude: z.number().optional(),
        longitude: z.number().optional(),
      }),
      customerSupportPhone: phoneSchema,
      geocodingScore: z.number().min(1, t('sites.create.validation.geocodingRequired')),
      geocodingSource: z.enum(['autocomplete', 'manual']).optional(),
      name: z.string().trim().min(1, t('sites.settings.validation.nameRequired')),
      phone: phoneSchema,
      spaceId: z.string(),
      type: z.enum(Object.values(SITE_TYPE) as [SiteType, ...SiteType[]], {
        error: t('sites.settings.validation.typeRequired'),
      }),
    });
  }, [t]);

  const form = useForm<UpdateSiteFormInput>({
    defaultValues: toDefaultValues(site),
    mode: 'all',
    resolver: zodResolver(schema),
  });

  useResetFormOnSiteUpdate(form, site, toDefaultValues);

  // ─── Submit ───────────────────────────────────────────────────────────────

  const onSubmit = useCallback(
    (data: UpdateSiteFormInput): void => {
      const location = hasAddressData(data.address)
        ? buildSiteLocation({
            address: data.address,
            coordinates: data.coordinates,
            source: data.geocodingSource ?? 'manual',
          })
        : undefined;

      updateSite
        .mutateAsync({
          code: data.code,
          color: data.color,
          ...(hasPhoneData(data.customerSupportPhone)
            ? { customerSupportPhone: data.customerSupportPhone }
            : { customerSupportPhone: null }),
          ...(location !== undefined ? { location } : {}),
          name: data.name,
          ...(hasPhoneData(data.phone) ? { phone: data.phone } : { phone: null }),
          type: data.type,
        })
        .then(
          () => {
            toast.success(t('sites.settings.updateSuccess'));
          },
          (error: unknown) => {
            const detail = getApiErrorDetail(error);
            if (detail.statusCode === 409) {
              if (detail.errorCode === 'Conflict.DuplicateSiteCode') {
                form.setError('code', { message: t('sites.settings.validation.codeExists') });
              } else if (detail.errorCode === 'Conflict.DuplicateSiteName') {
                form.setError('name', { message: t('sites.settings.validation.nameExists') });
              }
              return;
            }
            toast.error(detail.message || t('sites.settings.updateError'));
          },
        );
    },
    [form, t, updateSite],
  );

  const [resetKey, setResetKey] = useState(0);

  const onCancel = useCallback((): void => {
    form.reset();
    setResetKey((k) => k + 1);
  }, [form]);

  return {
    form,
    isDirty: form.formState.isDirty,
    isPending: updateSite.isPending,
    onCancel,
    onSubmit,
    resetKey,
  };
}
